import React, { useCallback, useState } from 'react';
import { getRoleBadge } from '../utils/roles';
import { Row, Col, Card, Button, Badge, Modal, Form, Dropdown, Alert, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { getUsers, usePostUsers, usePutUsersId, useDeleteUsersId, useGetUsersId, getGetAuthMeQueryKey } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { User } from '../api/generated/model';
import type { GetUsersParams } from '../api/generated/model/getUsersParams';
import { useQueryClient } from '@tanstack/react-query';
import { CreateUserRequest } from '../api/generated/model/createUserRequest';
import { UserRole } from '../api/generated/model/userRole';
import { UpdateUserRequest } from '../api/generated/model/updateUserRequest';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';

const FETCH_LIMIT = 100;

const UserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formData, setFormData] = useState<CreateUserRequest>({
    first_name: '',
    last_name: '',
    email: '',
    role: '' as any,
    territory: '',
    manager_id: '',
    password: ''
  });
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [showPassword, setShowPassword] = useState(false);
  const [sortField, setSortField] = useState('first_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { data: userData } = useGetUsersId(currentUser?.id || '', { query: { enabled: !!currentUser?.id } });

  // Cursor-paginated user list. First page sends filters/sort; subsequent
  // pages send only the cursor (it encodes the rest server-side).
  const buildQueryParams = useCallback((cursor: string | undefined) => {
    if (cursor) {
      return { cursor, limit: FETCH_LIMIT };
    }
    const base: Record<string, any> = {
      sort_field: sortField,
      sort_direction: sortDir,
      limit: FETCH_LIMIT,
    };
    if (roleFilter) base.role = roleFilter;
    return base;
  }, [roleFilter, sortField, sortDir]);

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((u: User) => u.id || '', []);

  const {
    allItems: users,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
    setAllItems: setUsers,
  } = usePaginatedQuery<User, any, undefined>({
    queryKey: 'users',
    buildQueryParams,
    queryFn: (params) => getUsers(params as GetUsersParams),
    getRowKey,
  });

  const refetch = useCallback(() => {
    // Reset accumulated pages and force a fresh first-page fetch.
    setUsers([]);
    queryClient.invalidateQueries({ queryKey: ['users'] });
  }, [queryClient, setUsers]);

  // Create user mutation
  const createUserMutation = usePostUsers({
    mutation: {
      onSuccess: () => {
        setShowAddModal(false);
        resetForm();
        refetch();
      }
    }
  });

  // Update user mutation
  const updateUserMutation = usePutUsersId({
    mutation: {
      onSuccess: () => {
        setShowAddModal(false);
        resetForm();
        refetch();
      }
    }
  });

  // Delete user mutation
  const deleteUserMutation = useDeleteUsersId({
    mutation: {
      onSuccess: () => {
        refetch();
      }
    }
  });

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      role: '' as any,
      territory: '',
      manager_id: '',
      password: ''
    });
    setSelectedUser(null);
    setShowPassword(false);
  };

  const handleInputChange = (field: keyof CreateUserRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.role) {
      return;
    }

    if (selectedUser) {
      // Update existing user
      const updateData: UpdateUserRequest = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        manager_id: formData.manager_id || null,
      };

      if (formData.territory) {
        updateData.territory = formData.territory;
      }

      if (formData.password) {
        updateData.password = formData.password;
      }

      updateUserMutation.mutate({ id: selectedUser.id, data: updateData });
    } else {
      // Create new user
      const requestData: CreateUserRequest = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role: formData.role,
      };

      if (formData.territory) {
        requestData.territory = formData.territory;
      }

      if (formData.manager_id) {
        requestData.manager_id = formData.manager_id;
      }

      if (formData.password) {
        requestData.password = formData.password;
      }

      createUserMutation.mutate({ data: requestData });
    }
  };


  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      role: user.role || '',
      territory: user.territory || '',
      manager_id: user.manager_id || '',
      password: ''
    });
    setShowAddModal(true);
  };

  const handleAddUser = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleResetPassword = (user: any) => {
    const newPassword = prompt(`Enter new temporary password for ${user.first_name} ${user.last_name}:`);
    if (newPassword && newPassword.trim() !== '') {
      updateUserMutation.mutate({
        id: user.id,
        data: { password: newPassword }
      });
    }
  };

  const handleToggleActive = (user: any) => {
    const action = user.active ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} ${user.first_name} ${user.last_name}?`)) {
      updateUserMutation.mutate({
        id: user.id,
        data: { active: !user.active }
      });
    }
  };

  const handleDeleteUser = (user: any) => {
    if (window.confirm(`Are you sure you want to delete ${user.first_name} ${user.last_name}? This action cannot be undone.`)) {
      deleteUserMutation.mutate({ id: user.id });
    }
  };

  const handleImpersonate = (user: any) => {
    document.cookie = `impersonate=${user.id}; path=/`;
    window.location.reload();
  };

  const stickyHeaderContent = (
    <Row className="mb-3">
      <Col md={6}>
        <Button variant="primary" onClick={handleAddUser}>
          <i className="bi bi-plus-lg me-1"></i>
          Add User
        </Button>
      </Col>
      <Col md={6} className="text-end">
        <Form.Select
          style={{ width: 'auto', display: 'inline-block' }}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
        >
          <option value="">All Roles</option>
          <option value="none">None (Pending Approval)</option>
          <option value="admin">Admin</option>
          <option value="vp_sales">VP of Sales</option>
          <option value="sales_manager">Sales Manager</option>
          <option value="salesperson">Salesperson</option>
          <option value="finance">Finance</option>
          <option value="events_manager">Events Manager</option>
          <option value="marketing">Marketing</option>
        </Form.Select>
      </Col>
    </Row>
  );

  const userColumns: ColumnConfig<User>[] = [
    { key: 'first_name', label: 'User', width: 250, sortable: true, sortValue: (u) => `${u.first_name} ${u.last_name}`, renderCell: (u) => (
      <div className="d-flex align-items-center">
        <div className="bg-primary text-white me-2 d-flex align-items-center justify-content-center flex-shrink-0"
             style={{ width: '32px', height: '32px', borderRadius: '50%', fontSize: '12px', fontWeight: 'bold' }}>
          {`${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`}
        </div>
        <div>
          <div className="fw-semibold">{`${u.first_name} ${u.last_name}`}</div>
          <small className="text-muted">{u.email}</small>
        </div>
      </div>
    ) },
    { key: 'role', label: 'Role', width: 120, sortable: true, renderCell: (u) => u.role ? getRoleBadge(u.role) : <span className="text-muted">-</span> },
    { key: 'manager_id', label: 'Manager', width: 150, sortable: true, sortValue: (u) => {
      const mgr = users.find(m => m.id === u.manager_id);
      return mgr ? `${mgr.first_name} ${mgr.last_name}` : '';
    }, renderCell: (u) => {
      const mgr = users.find(m => m.id === u.manager_id);
      return mgr ? <>{mgr.first_name} {mgr.last_name}</> : <span className="text-muted">-</span>;
    } },
    { key: 'territory', label: 'Territory', width: 120, sortable: true, renderCell: (u) => u.territory ? <Badge bg="light" text="dark">{u.territory}</Badge> : <span className="text-muted">-</span> },
    { key: 'active', label: 'Status', width: 80, sortable: true, sortValue: (u) => u.active ? 1 : 0, renderCell: (u) => <Badge bg={u.active ? 'success' : 'secondary'}>{u.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'created_at', label: 'Created', width: 100, sortable: true, renderCell: (u) => <small className="text-muted">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</small> },
    { key: 'actions', label: 'Actions', width: 60, renderCell: (u) => (
      <Dropdown onClick={(e: any) => e.stopPropagation()}>
        <Dropdown.Toggle variant="outline-secondary" size="sm" className="border-0">
          <i className="bi bi-three-dots"></i>
        </Dropdown.Toggle>
        <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }} style={{ transition: 'none' }}>
          <Dropdown.Item onClick={() => handleEditUser(u)}><i className="bi bi-pencil me-2"></i>Edit User</Dropdown.Item>
          <Dropdown.Item onClick={() => handleResetPassword(u)}><i className="bi bi-key me-2"></i>Reset Password</Dropdown.Item>
          {currentUser?.id !== u.id && (
            <Dropdown.Item onClick={() => handleImpersonate(u)}><i className="bi bi-person-badge me-2"></i>Impersonate</Dropdown.Item>
          )}
          <Dropdown.Divider />
          <Dropdown.Item className={u.active ? 'text-warning' : 'text-success'} onClick={() => handleToggleActive(u)}>
            <i className={`bi ${u.active ? 'bi-pause' : 'bi-play'} me-2`}></i>{u.active ? 'Deactivate' : 'Activate'}
          </Dropdown.Item>
          <Dropdown.Item className="text-danger" onClick={() => handleDeleteUser(u)}>
            <i className="bi bi-trash me-2"></i>Delete User
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    ) },
  ];

  return (
    <DashboardLayout
      title="User Management"
      subtitle="Manage user accounts, roles, and permissions"
      stickyHeader={stickyHeaderContent}
      scrollable
    >
      {error && (
        <Alert variant="danger" dismissible>
          <Alert.Heading>Error Loading Users</Alert.Heading>
          <p>{error instanceof Error ? error.message : 'Failed to load users'}</p>
        </Alert>
      )}

      {deleteUserMutation.isError && (
        <Alert variant="danger" dismissible onClose={() => deleteUserMutation.reset()}>
          <Alert.Heading>Error Deleting User</Alert.Heading>
          <p>{(deleteUserMutation.error as any)?.response?.data?.message || (deleteUserMutation.error as any)?.response?.data?.error || 'Failed to delete user'}</p>
        </Alert>
      )}

      {deleteUserMutation.isSuccess && (
        <Alert variant="success" dismissible onClose={() => deleteUserMutation.reset()}>
          User deleted successfully!
        </Alert>
      )}

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <h5 className="mb-0">
            <i className="bi bi-people-fill me-2"></i>
            User Management
          </h5>
        </Card.Header>
        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={users}
            columns={userColumns}
            getRowKey={getRowKey}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
            preferencesKey="user_management"
            userId={currentUser?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            isLoading={isLoading}
            enableStickyHeader={true}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            emptyState={<div className="text-center py-4 text-muted">No users found</div>}
          />
        </Card.Body>
      </Card>

      {/* Add/Edit User Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedUser ? 'Edit User' : 'Add New User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(createUserMutation.isError || updateUserMutation.isError) && (
            <Alert variant="danger" dismissible onClose={() => {
              createUserMutation.reset();
              updateUserMutation.reset();
            }}>
              <Alert.Heading>{selectedUser ? 'Error Updating User' : 'Error Creating User'}</Alert.Heading>
              <p>
                {(createUserMutation.error as any)?.response?.data?.message ||
                 (createUserMutation.error as any)?.response?.data?.error ||
                 (updateUserMutation.error as any)?.response?.data?.message ||
                 (updateUserMutation.error as any)?.response?.data?.error ||
                 (selectedUser ? 'Failed to update user' : 'Failed to create user')}
              </p>
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Enter last name"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
                required
                disabled={!!selectedUser}
              />
              {selectedUser && (
                <Form.Text className="text-muted">
                  Email cannot be changed after user creation.
                </Form.Text>
              )}
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    required
                  >
                    <option value="">Select Role</option>
                    <option value="none">None (No Access)</option>
                    <option value="admin">Admin</option>
                    <option value="vp_sales">VP of Sales</option>
                    <option value="sales_manager">Sales Manager</option>
                    <option value="salesperson">Salesperson</option>
                    <option value="finance">Finance</option>
                    <option value="events_manager">Events Manager</option>
                    <option value="marketing">Marketing</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Territory</Form.Label>
                  <Form.Select
                    value={formData.territory || ''}
                    onChange={(e) => handleInputChange('territory', e.target.value)}
                  >
                    <option value="">Select Territory</option>
                    <option value="Northeast">Northeast</option>
                    <option value="Southeast">Southeast</option>
                    <option value="Midwest">Midwest</option>
                    <option value="West">West</option>
                    <option value="Global">Global</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Manager</Form.Label>
              <Form.Select
                value={formData.manager_id || ''}
                onChange={(e) => handleInputChange('manager_id', e.target.value)}
              >
                <option value="">No Manager</option>
                {users.filter(u => (u.id !== selectedUser?.id || selectedUser?.role === 'admin') && u.active).map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.role?.replace('_', ' ')})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {selectedUser ? 'New Password (optional)' : 'Temporary Password'}
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password || ''}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={selectedUser ? 'Leave blank to keep current password' : 'Enter temporary password'}
                  style={{ paddingRight: '40px' }}
                />
                <Button
                  variant="link"
                  className="position-absolute top-0 end-0"
                  style={{ zIndex: 10 }}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                </Button>
              </div>
              <Form.Text className="text-muted">
                {selectedUser
                  ? 'Only enter a new password if you want to change it.'
                  : 'User will be required to change password on first login.'}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAddModal(false)}
            disabled={createUserMutation.isPending || updateUserMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={createUserMutation.isPending || updateUserMutation.isPending}
          >
            {(createUserMutation.isPending || updateUserMutation.isPending) ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {selectedUser ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              selectedUser ? 'Update User' : 'Create User'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default UserManagement;